import { useCallback, useEffect, useState } from 'react';
import { Bug, Lightbulb, MessageSquareText, Pencil, Plus, Star, MessagesSquare } from 'lucide-react';
import FeedbackModal from '../../components/common/FeedbackModal';
import { FeedbackItem, getMyFeedback } from '../../services/api/feedbackService';
import { formatDateTime } from '../../utils/useUtil';

const TYPE_ICONS: Record<string, typeof Bug> = {
  Bug: Bug,
  Suggestion: Lightbulb,
  Rating: Star,
  General: MessageSquareText,
};

function FeedbackTypeIcon({ typeName }: { typeName?: string }) {
  const Icon = TYPE_ICONS[typeName || ''] || MessageSquareText;
  return (
    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary">
      <Icon className="h-4.5 w-4.5" />
    </span>
  );
}

export default function MyFeedbackPage() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<FeedbackItem | null>(null);

  const loadFeedback = useCallback(async () => {
    setIsLoading(true);
    setError('');
    const result = await getMyFeedback();
    if (result.success) {
      setItems(result.data || []);
    } else {
      setError(result.message || 'Failed to load your feedback.');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const openCreateModal = () => {
    setEditingFeedback(null);
    setModalOpen(true);
  };

  const openEditModal = (item: FeedbackItem) => {
    setEditingFeedback(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingFeedback(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feedback &amp; Suggestions</h1>
          <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
            Share bugs, ideas, and thoughts to help us improve ISentryx, and track everything you've submitted here.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-hover"
        >
          <Plus className="h-4 w-4" />
          Feedback
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
      )}

      {isLoading ? (
        <div className="space-y-3" aria-label="Loading feedback">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex animate-pulse gap-3">
                <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-gray-200" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 w-1/3 rounded bg-gray-200" />
                  <div className="h-3 w-full rounded bg-gray-100" />
                  <div className="h-3 w-2/3 rounded bg-gray-100" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
            <MessagesSquare className="h-6 w-6" />
          </span>
          <p className="mt-4 text-lg font-semibold text-gray-900">You haven't submitted any feedback yet.</p>
          <p className="mt-1 max-w-sm text-sm text-gray-500">
            Report a bug, share a suggestion, or rate the app to help us improve ISentryx.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const canEdit = item.statusName === 'New';
            return (
              <div key={item.feedbackId} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                <div className="flex items-start gap-3">
                  <div className="flex min-w-0 flex-1 gap-3">
                    <FeedbackTypeIcon typeName={item.feedbackTypeName} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.feedbackTypeName || 'Feedback'}
                        </span>
                        {item.rating ? (
                          <span className="text-sm text-yellow-500">{'★'.repeat(item.rating)}</span>
                        ) : null}
                      </div>
                      {item.subject && (
                        <p className="mt-1 text-sm font-medium text-gray-800">{item.subject}</p>
                      )}
                      <p className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap break-words text-sm text-gray-600">{item.message}</p>
                      <p className="mt-2 text-xs text-gray-400">
                        Submitted On: {formatDateTime(item.createdAt)}
                      </p>
                      {item.adminNotes && (
                        <div className="mt-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                          <span className="font-semibold text-gray-700">Admin notes: </span>
                          {item.adminNotes}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* {canEdit && (
                    <button
                      onClick={() => openEditModal(item)}
                      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  )} */}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FeedbackModal
        key={editingFeedback?.feedbackId ?? 'new'}
        isOpen={modalOpen}
        onClose={closeModal}
        feedback={editingFeedback}
        onSaved={loadFeedback}
      />
    </div>
  );
}
